import { useState, useEffect, useMemo } from 'react';
import { Loader2 } from 'lucide-react';

// Import tab components
import { DefaultTab } from '../components/dashboard/DefaultTab';
import { ScheduledTab } from '../components/dashboard/ScheduledTab';
import { SpotOrdersTab } from '../components/dashboard/SpotOrdersTab';
import { SalesOrdersTab } from '../components/dashboard/SalesOrdersTab';
import { CachedPerformanceTab } from '../components/dashboard/CachedPerformanceTab';
import { GroupKPITab } from '../components/dashboard/GroupKPITab';
import { GroupDetailKPITab } from '../components/dashboard/GroupDetailKPITab';
import { SiteKPITab } from '../components/dashboard/SiteKPITab';
import { CostTab } from '../components/dashboard/CostTab';
import { EmissionsTab } from '../components/dashboard/EmissionsTab';
import { ComplianceTab } from '../components/dashboard/ComplianceTab';
import { FutureForecastTab } from '../components/dashboard/FutureForecastTab';
import { ForecastForwardTab } from '../components/dashboard/ForecastForwardTab';
import { ExpeditingTab } from '../components/dashboard/ExpeditingTab';
import { PerformanceRatingsTab } from '../components/dashboard/PerformanceRatingsTab';
import { DateAdherenceTab } from '../components/dashboard/DateAdherenceTab';

// Import dashboard UI components
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardFilters } from '../components/dashboard/DashboardFilters';
import { SaveFilterDialog } from '../components/dashboard/SaveFilterDialog';
import { Toast } from '../components/dashboard/Toast';

// Import types
import {
  CONFIGURABLE_DASHBOARD_TAB_IDS,
  DASHBOARD_TAB_REGISTRY,
  DEFAULT_DASHBOARD_TAB_ID,
} from '../components/dashboard/types';

// Import hooks and contexts
import { useAuth } from '../contexts/AuthContext';
import { useView } from '../contexts/ViewContext';
import { useMenuSettings } from '../contexts/MenuSettingsContext';
import { usePortalMode } from '../hooks/usePortalMode';
import { useDashboardData } from '../hooks/useDashboardData';

// Dashboard tabs — driven from shared registry
const tabs = DASHBOARD_TAB_REGISTRY;

export default function Dashboard() {
  const { user } = useAuth();
  const { mainMenuType, isComplianceMode, isDashboardTabEnabled, loadSettingsFromBackend } = useMenuSettings();
  const { viewLevel, selectedCompanyName, selectedSiteName, selectedGroupName, selectedGroupId, accessibleCompanies } = useView();
  const { isCustomer: portalIsCustomer, supportsDualMode: portalSupportsDualMode } = usePortalMode();

  const [activeTab, setActiveTab] = useState(
    isComplianceMode ? 'compliance' : DEFAULT_DASHBOARD_TAB_ID,
  );
  const [showFilters, setShowFilters] = useState(false);

  // CR-4441: Refresh settings on mount to pick up any admin changes
  useEffect(() => {
    loadSettingsFromBackend();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dashboard = useDashboardData(isComplianceMode);

  // CR-4349: Derive unique divisions from accessible companies for the filter dropdown.
  // CR-4887: ViewContext.accessibleCompanies stays as the CUSTOMER hierarchy
  // (the user-menu site switcher reads it). Dashboard divisions need the
  // SUPPLIER hierarchy when the user is on the customer portal — use the
  // dashboard hook's suppliers list, which is fetched with
  // ?context=supplier_filter and contains the supplier companies served.
  const divisionSource = portalIsCustomer ? dashboard.suppliers : accessibleCompanies;
  const divisions = useMemo(() => {
    const divMap = new Map<number, string>();
    for (const company of divisionSource) {
      const rec = company as unknown as Record<string, unknown>;
      const groupId = rec['group'] as number | undefined;
      const groupName = rec['group_name'] as string | undefined;
      if (groupId && groupName) {
        divMap.set(groupId, groupName);
      }
    }
    return Array.from(divMap.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [divisionSource]);

  const isAdminUser = user?.is_superuser || user?.is_staff;
  const effectiveIsCustomer = (isAdminUser && !portalSupportsDualMode)
    ? mainMenuType === '2'
    : portalIsCustomer;

  const visibleTabs = tabs.filter(tab => {
    if (isComplianceMode) {
      if (tab.id === 'compliance') return true;
      return CONFIGURABLE_DASHBOARD_TAB_IDS.has(tab.id)
        && isDashboardTabEnabled(tab.id, effectiveIsCustomer);
    }

    if (!CONFIGURABLE_DASHBOARD_TAB_IDS.has(tab.id)) return false;
    // Forecast Forward is operating-org only regardless of customer-column toggle.
    if (effectiveIsCustomer && tab.id === 'forecast-forward') return false;
    return isDashboardTabEnabled(tab.id, effectiveIsCustomer);
  });

  // CR-4166 & CR-4441: If active tab is no longer visible, fallback.
  // In compliance mode, always prefer compliance tab as default.
  useEffect(() => {
    if (visibleTabs.length > 0 && !visibleTabs.some(t => t.id === activeTab)) {
      const complianceTab = isComplianceMode
        ? visibleTabs.find(t => t.id === 'compliance')
        : undefined;
      setActiveTab(complianceTab ? complianceTab.id : visibleTabs[0].id);
    }
  }, [visibleTabs, activeTab, isComplianceMode]);

  // When toggling supplier ↔ customer view, land on the customer Sales
  // Dashboard tab (same as native customer users).
  useEffect(() => {
    if (effectiveIsCustomer && activeTab === 'forecast-forward') {
      setActiveTab(DEFAULT_DASHBOARD_TAB_ID);
    }
  }, [effectiveIsCustomer, activeTab]);

  // Lazy-load per-tab data slices on demand — keeps the default dashboard
  // payload small. Each loader is sticky: once fetched, subsequent refreshes
  // keep the section hydrated.
  useEffect(() => {
    if (activeTab === 'date-adherence') {
      dashboard.loadDateAdherence();
    } else if (activeTab === 'site-kpi') {
      dashboard.loadCompanyKpi();
    } else if (activeTab === 'group-kpi' || activeTab === 'group-detail-kpi') {
      dashboard.loadGroupKpi();
    } else if (activeTab === 'cost') {
      dashboard.loadCostData();
    }
  }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const currency = { currentCurrency: dashboard.filters.currency };

  const handleDrillDown = (_type: string, _data?: Record<string, unknown>) => {
    // Drill-down navigation placeholder
  };

  const renderTabContent = () => {
    if (activeTab === 'sales-orders') {
      return <SalesOrdersTab />;
    }
    if (activeTab === 'forecast-forward') {
      return <ForecastForwardTab />;
    }

    if (!dashboard.data) {
      // Only show error if there's an explicit error AND we're not loading.
      // Without an error, data is just not yet available (race between effects).
      if (dashboard.error && !dashboard.loading) {
        return (
          <div className="p-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-800 font-medium">Failed to load dashboard data</p>
              <p className="text-red-600 text-sm mt-1">{dashboard.error}</p>
              <button onClick={() => dashboard.fetchDashboardData(true, undefined, true)} className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">Retry</button>
            </div>
          </div>
        );
      }
      // Still loading or between fetches — show inline spinner
      return (
        <div className="p-6 flex items-center justify-center min-h-[300px]">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="mt-2 text-sm text-muted-foreground">Loading dashboard data...</p>
          </div>
        </div>
      );
    }

    switch (activeTab) {
      case 'default':
        return <DefaultTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} dataPeriod={dashboard.dataPeriod} orderType={dashboard.orderType} />;
      case 'scheduled':
        return <ScheduledTab />;
      case 'spot-orders':
        return <SpotOrdersTab />;
      case 'sales-orders':
        return <SalesOrdersTab />;
      case 'cached-performance':
        return <CachedPerformanceTab />;
      case 'group-kpi':
        return <GroupKPITab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      case 'group-detail-kpi':
        return <GroupDetailKPITab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      case 'site-kpi':
        return <SiteKPITab
          data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown}
          onCustomerTypeChange={(customerType) => {
            dashboard.setFilters(prev => ({ ...prev, customerType }));
            setTimeout(() => dashboard.fetchDashboardData(true), 100);
          }}
        />;
      case 'cost':
        return <CostTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} dataPeriod={dashboard.dataPeriod} orderType={dashboard.orderType} />;
      case 'emissions':
        return <EmissionsTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} dataPeriod={dashboard.dataPeriod} orderType={dashboard.orderType} />;
      case 'compliance':
        return <ComplianceTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} dataPeriod={dashboard.dataPeriod} orderType={dashboard.orderType} />;
      case 'future-forecast':
        return <FutureForecastTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      case 'date-adherence':
        return <DateAdherenceTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      case 'expediting':
        return <ExpeditingTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      case 'performance-ratings':
        return <PerformanceRatingsTab data={dashboard.data} currency={currency} filters={dashboard.filters} onDrillDown={handleDrillDown} />;
      default:
        return null;
    }
  };

  const isSelfFetchingDashboardTab = CONFIGURABLE_DASHBOARD_TAB_IDS.has(activeTab);

  if (dashboard.loading && !isSelfFetchingDashboardTab) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading dashboard data from database...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b">
        <DashboardHeader
          savedFilters={dashboard.savedFilters}
          activeFilterPresetId={dashboard.activeFilterPresetId}
          showFilters={showFilters}
          setShowFilters={setShowFilters}
          onLoadPreset={dashboard.loadFilterPreset}
          onRefresh={() => dashboard.fetchDashboardData(true, undefined, true)}
          onExportReport={() => dashboard.handleExportReport(activeTab)}
          exporting={dashboard.exporting}
          viewLevel={viewLevel}
          selectedGroupId={selectedGroupId}
          viewLevelName={
            viewLevel === 'GROUP' ? selectedGroupName || undefined :
            viewLevel === 'SITE' ? selectedSiteName || undefined :
            selectedCompanyName || undefined
          }
        />

        {/* Filters Panel */}
        {showFilters && (
          <DashboardFilters
            filters={dashboard.filters}
            setFilters={dashboard.setFilters}
            customers={dashboard.customers}
            suppliers={dashboard.suppliers}
            sites={dashboard.sites}
            warehouses={dashboard.warehouses}
            customerGroups={dashboard.customerGroups}
            commodityGroups={dashboard.commodityGroups}
            commodityTypes={dashboard.commodityTypes}
            divisions={divisions}
            preferencesCurrency={dashboard.filters.currency}
            onApplyFilters={dashboard.handleApplyFilters}
            onResetFilters={dashboard.handleResetFilters}
            onShowSaveDialog={() => dashboard.setShowSaveFilterDialog(true)}
            onAutoCollapse={() => setShowFilters(false)}
            // CR-4800
            isComplianceMode={activeTab === 'compliance'}
          />
        )}

        {/* Tabs — hidden when supplier has a single Forecast view */}
        <div className="px-6">
          {visibleTabs.length > 1 && (
          <div className="flex space-x-6 border-b">
            {visibleTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`pb-3 px-1 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary font-medium'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          )}

          {/* CR-3930 + CR-3931: Weekly/Monthly + Orders Toggle.
              CR-4757: Compliance tab ignores dataPeriod (the prop is
              destructured as `_dataPeriod` in ComplianceTab) so the toggle
              was a no-op there — and on MAB (compliance-only tenant) it was
              the only thing showing above an unaffected dashboard. Removing
              'compliance' from the eligible tabs hides it for every tenant
              when they're on Compliance, including MAB. */}
          {['default', 'cost', 'emissions'].includes(activeTab) && (
            <div className="flex items-center gap-4 mt-3">
              <span className="text-xs text-muted-foreground mr-2">View:</span>
              <button
                onClick={() => dashboard.setDataPeriod('weekly')}
                className={`px-3 py-1 text-xs font-medium rounded-l-md border transition-colors ${
                  dashboard.dataPeriod === 'weekly'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted'
                }`}
              >
                Weekly
              </button>
              <button
                onClick={() => dashboard.setDataPeriod('monthly')}
                className={`px-3 py-1 text-xs font-medium rounded-r-md border-t border-r border-b transition-colors ${
                  dashboard.dataPeriod === 'monthly'
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:bg-muted'
                }`}
              >
                Monthly
              </button>

              {/* CR-4345: Orders toggle removed — order type available via filters */}
            </div>
          )}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {renderTabContent()}
      </div>

      {/* Save Filter Dialog */}
      <SaveFilterDialog
        isOpen={dashboard.showSaveFilterDialog}
        onClose={() => dashboard.setShowSaveFilterDialog(false)}
        onSave={dashboard.handleSaveFilter}
        filterName={dashboard.filterName}
        setFilterName={dashboard.setFilterName}
        filterDescription={dashboard.filterDescription}
        setFilterDescription={dashboard.setFilterDescription}
        isDefault={dashboard.isDefault}
        setIsDefault={dashboard.setIsDefault}
        isShared={dashboard.isShared}
        setIsShared={dashboard.setIsShared}
        filters={dashboard.filters}
        customers={dashboard.customers}
        suppliers={dashboard.suppliers}
        sites={dashboard.sites}
      />

      {/* Toast Notification */}
      <Toast
        message={dashboard.toast.message}
        type={dashboard.toast.type}
        visible={dashboard.toast.visible}
        onClose={() => dashboard.setToast((prev) => ({ ...prev, visible: false }))}
      />
    </div>
  );
}
